<template>
  <!-- The whole resume as JSON, in a code editor on a panel that slides in from the right.
       Valid changes are applied to the resume as you type (and saved like any edit); changes made
       in the forms show here. Invalid JSON is only reported: the resume keeps its last good state. -->
  <Transition enter-active-class="transition-opacity duration-200 ease-out" enter-from-class="opacity-0" leave-active-class="transition-opacity duration-150 ease-in" leave-to-class="opacity-0">
    <div v-if="open" class="absolute inset-0 z-20 bg-slate-900/30 dark:bg-black/50" aria-hidden="true" @click="$emit('close')"></div>
  </Transition>

  <Transition enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]" enter-from-class="translate-x-full"
    leave-active-class="transition-transform duration-200 ease-in" leave-to-class="translate-x-full" @after-enter="focusEditor">
    <aside v-if="open" role="dialog" aria-label="Resume JSON" @keydown.esc="$emit('close')"
      class="absolute inset-y-0 right-0 z-30 w-full sm:w-[70%] flex flex-col bg-white dark:bg-[#1C2128] border-l border-gray-200 dark:border-gray-700 shadow-[-12px_0_32px_-12px_rgba(15,23,42,0.35)]">
      <header class="flex-shrink-0 flex items-center gap-3 px-5 h-14 border-b border-gray-100 dark:border-gray-700">
        <span class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 inline-flex items-center justify-center flex-shrink-0">
          <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="ICONS.json"/></svg>
        </span>
        <div class="min-w-0">
          <h2 class="text-sm font-semibold leading-tight text-gray-900 dark:text-white">Resume JSON</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ store.name }} · {{ size }}</p>
        </div>

        <span class="ml-auto inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[11px] font-semibold"
          :class="problem ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'">
          <span class="w-1.5 h-1.5 rounded-full" :class="problem ? 'bg-red-500' : 'bg-green-500'" aria-hidden="true"></span>
          {{ problem ? 'Not applied' : 'Valid' }}
        </span>
        <IconButton :icon="ICONS.close" label="Close" tone="plain" align="right" @click="$emit('close')" />
      </header>

      <div class="flex-shrink-0 flex items-center gap-1 px-4 h-11 border-b border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-[#161b22]">
        <IconButton :icon="ICONS.collapse" label="Collapse all levels" tone="plain" :disabled="!ready" @click="controller?.collapseAll()" />
        <IconButton :icon="ICONS.expand" label="Expand all levels" tone="plain" :disabled="!ready" @click="controller?.expandAll()" />
        <span class="w-px h-5 mx-1 bg-gray-200 dark:bg-gray-700" aria-hidden="true"></span>
        <IconButton :icon="ICONS.beautify" label="Beautify" tone="plain" :disabled="!ready || Boolean(parseError)" @click="beautify" />
        <IconButton :icon="copied ? ICONS.check : ICONS.copy" :label="copied ? 'Copied' : 'Copy JSON'" tone="plain" :disabled="!ready" @click="copy" />
      </div>

      <div class="flex-1 min-h-0 relative">
        <div ref="host" class="absolute inset-0"></div>

        <!-- While the editor loads -->
        <div v-if="!ready && !loadError" class="absolute inset-0 p-5 space-y-2.5 animate-pulse bg-white dark:bg-[#0d1117]" aria-hidden="true">
          <div v-for="n in 14" :key="n" class="h-3.5 rounded bg-gray-100 dark:bg-gray-800" :style="{ width: `${30 + ((n * 37) % 55)}%`, marginLeft: `${(n % 4) * 16}px` }"></div>
        </div>
        <div v-if="loadError" class="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6 bg-white dark:bg-[#0d1117]">
          <p class="text-sm text-gray-600 dark:text-gray-300">The editor could not be loaded.</p>
          <button type="button" @click="mountEditor" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
        </div>
      </div>

      <footer class="flex-shrink-0 flex items-center gap-4 px-5 h-9 border-t border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-[#161b22] text-xs" role="status" aria-live="polite">
        <p v-if="problem" class="min-w-0 truncate text-red-600 dark:text-red-400" :title="problem">{{ problem }}</p>
        <p v-else class="min-w-0 truncate text-gray-500 dark:text-gray-400">Changes apply as you type. The photo is not part of the JSON.</p>
        <p class="ml-auto flex-shrink-0 tabular-nums text-gray-400 dark:text-gray-500">Ln {{ cursor.line }}, Col {{ cursor.column }}</p>
      </footer>
    </aside>
  </Transition>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useResumeEditorStore } from 'stores/resumeEditor.js'
import { TEMPLATES } from '../templates.js'
import IconButton from 'controllers/vue_components/shared/IconButton.vue'
import { ICONS } from './icons.js'
import { completeContent } from './resumeShape.js'

const props = defineProps({ open: { type: Boolean, default: false } })
defineEmits(['close'])

const TEMPLATE_IDS = TEMPLATES.map((template) => template.id)
const APPLY_DELAY = 350 // ms of quiet before typed JSON is applied

const store = useResumeEditorStore()
const host = ref(null)
const ready = ref(false)
const loadError = ref(false)
const copied = ref(false)
const cursor = reactive({ line: 1, column: 1 })
const parseError = ref('') // the text is not JSON
const shapeError = ref('') // it is JSON, but not a resume
const problem = computed(() => parseError.value || shapeError.value)
let controller = null
let focused = false
let timer = null

// The document as shown: the resume's title, the template and the content (the photo is its own upload). The title is `resume_title`, not
// `name`: a bare name would read as the person's name, which is content.basics.full_name.
const document_ = computed(() => ({ resume_title: store.name, template: store.resume.template, content: store.content }))
const pretty = (value) => JSON.stringify(value, null, 2)
const size = computed(() => `${(new Blob([pretty(document_.value)]).size / 1024).toFixed(1)} KB`)

// The editor is loaded on first use (it is a separate download) and made each time the panel opens.
async function mountEditor() {
  loadError.value = false
  ready.value = false
  try {
    const { createJsonEditor } = await import('./jsonEditor.js')
    await nextTick()
    if (!props.open || !host.value) return // closed while it loaded

    controller = createJsonEditor({
      parent: host.value,
      doc: pretty(document_.value),
      onChange: onTyped,
      onCursor: ({ line, column }) => Object.assign(cursor, { line, column }),
      onFocus: onFocusChange,
    })
    ready.value = true
  } catch {
    loadError.value = true
  }
}

function destroyEditor() {
  clearTimeout(timer)
  controller?.destroy()
  controller = null
  ready.value = false
  focused = false
  parseError.value = ''
  shapeError.value = ''
}

watch(() => props.open, (isOpen) => {
  if (isOpen) nextTick(mountEditor)
  else destroyEditor()
}, { immediate: true })

// Once the panel has slid into place (focusing sooner would scroll the page while it is off screen).
const focusEditor = () => controller?.focus()

// A change made somewhere else (a form, the Design panel) shows here, unless the user is typing in the editor.
watch(() => JSON.stringify(document_.value), () => {
  if (controller && !focused) controller.setText(pretty(document_.value))
})

function onTyped() {
  clearTimeout(timer)
  timer = setTimeout(apply, APPLY_DELAY)
}

// Leaving the editor shows what the resume really holds, unless the text is still broken (then it is kept to fix).
function onFocusChange(hasFocus) {
  focused = hasFocus
  if (hasFocus || !controller) return

  clearTimeout(timer)
  apply()
  if (!problem.value) controller.setText(pretty(document_.value))
}

// "Unexpected token } in JSON at position 41" -> add where that is, as line and column.
function describe(error, source) {
  const position = /position (\d+)/.exec(error.message)?.[1]
  if (position === undefined) return error.message
  const before = source.slice(0, Number(position))
  const line = before.split('\n').length
  const column = before.length - before.lastIndexOf('\n')
  return `${error.message} (line ${line}, column ${column})`
}

function apply() {
  if (!controller) return
  const source = controller.text()
  let parsed
  try {
    parsed = JSON.parse(source)
  } catch (error) {
    parseError.value = describe(error, source)
    return
  }
  parseError.value = ''

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return fail('The top level must be an object with resume_title, template and content.')
  if (parsed.template !== undefined && !TEMPLATE_IDS.includes(parsed.template)) return fail(`Unknown template "${parsed.template}". Use one of: ${TEMPLATE_IDS.join(', ')}.`)
  // `name` is what the title used to be called in this JSON, so a document that still has it keeps working.
  const title = parsed.resume_title !== undefined ? parsed.resume_title : parsed.name
  if (title !== undefined && typeof title !== 'string') return fail('resume_title must be text.')
  if (parsed.content !== undefined && (parsed.content === null || typeof parsed.content !== 'object' || Array.isArray(parsed.content))) return fail('content must be an object.')
  shapeError.value = ''

  if (typeof title === 'string' && title.trim() && title !== store.name) store.setName(title)
  if (parsed.template && parsed.template !== store.resume.template) store.setTemplate(parsed.template)
  if (parsed.content !== undefined) {
    const next = completeContent(parsed.content, store.content)
    // Replaced in place, so the editor's watcher sees an edit and saves it.
    if (JSON.stringify(next) !== JSON.stringify(store.content)) Object.assign(store.content, next)
  }
}

function fail(message) {
  shapeError.value = message
}

// Applies the text, then lays it out with a two space indent.
function beautify() {
  clearTimeout(timer)
  apply()
  if (!parseError.value) controller.setText(pretty(JSON.parse(controller.text())))
}

async function copy() {
  try {
    await navigator.clipboard.writeText(controller.text())
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  } catch {
    controller?.view.dispatch({ selection: { anchor: 0, head: controller.view.state.doc.length } }) // no clipboard here: select it so Ctrl+C works
  }
}

onBeforeUnmount(destroyEditor)
</script>
